import { useEffect, useState } from 'react';
import { Layout } from '@/components/Layout';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useSEO } from '@/lib/useSEO';
import { formatDate } from '@/lib/format';
import { toast } from 'sonner';
import {
  Plug, RefreshCw, AlertTriangle, CheckCircle2, XCircle, Info,
  Save, FileText, Hash, CalendarDays,
} from 'lucide-react';

/* ─── UI helpers ───────────────────────────────────────────────────────── */
const LEVEL_STYLE = {
  info:     'bg-blue-50 text-blue-700 border-blue-200',
  warning:  'bg-amber-50 text-amber-800 border-amber-200',
  critical: 'bg-rose-50 text-rose-800 border-rose-200',
};
const STATUS_STYLE = {
  active:          'text-emerald-700 bg-emerald-50 border-emerald-200',
  degraded:        'text-amber-800 bg-amber-50 border-amber-200',
  inactive:        'text-slate-600 bg-slate-50 border-slate-200',
  quota_exceeded:  'text-rose-800 bg-rose-50 border-rose-200',
  error:           'text-rose-900 bg-rose-100 border-rose-300',
};
const pct = (v) => v == null ? '—' : `${v}%`;
const num = (v) => v == null ? '—' : Number(v).toLocaleString('es-CO');

function StatusPill({ status }) {
  const style = STATUS_STYLE[status] || STATUS_STYLE.inactive;
  return (
    <span className={`text-[10px] font-bold uppercase tracking-wider border px-2 py-0.5 ${style}`}>
      {status}
    </span>
  );
}

/* ─── Factus quota config panel ────────────────────────────────────────── */
function FactusQuotaPanel({ snapshot, onReload }) {
  const cfg = snapshot?.extra || {};
  const [plan, setPlan] = useState(snapshot?.plan || '');
  const [docs, setDocs] = useState(snapshot?.contracted || '');
  const [start, setStart] = useState(snapshot?.period_start || '');
  const [end, setEnd] = useState(snapshot?.period_end || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setPlan(snapshot?.plan || '');
    setDocs(snapshot?.contracted || '');
    setStart(snapshot?.period_start || '');
    setEnd(snapshot?.period_end || '');
  }, [snapshot?.plan, snapshot?.contracted, snapshot?.period_start, snapshot?.period_end]);

  const save = async () => {
    if (!plan || !docs || !start || !end) {
      toast.error('Completa plan, documentos, inicio y vencimiento');
      return;
    }
    setSaving(true);
    try {
      await api.post('/admin/integrations/factus/quota', {
        plan, contracted_docs: Number(docs),
        period_start: start, period_end: end,
      });
      toast.success('Plan Factus actualizado');
      onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error guardando el plan');
    } finally { setSaving(false); }
  };

  return (
    <div className="border border-slate-200 bg-white p-5" data-testid="factus-quota-panel">
      <div className="flex items-center gap-2">
        <FileText className="h-4 w-4 text-slate-500" />
        <h3 className="font-bold text-slate-900">Factus — Plan contratado</h3>
      </div>
      {!cfg.configured && (
        <p className="text-xs text-amber-700 mt-2">
          <AlertTriangle className="inline h-3 w-3 mr-1" />
          Aún no has configurado el plan. Hasta que lo hagas no se activará el guard de cupo ni las alertas de consumo.
        </p>
      )}
      <div className="grid sm:grid-cols-4 gap-3 mt-4">
        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Plan</label>
          <Input data-testid="factus-plan-input" value={plan} onChange={(e) => setPlan(e.target.value)} className="rounded-none mt-1" placeholder="basic / pro / enterprise" />
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Documentos contratados</label>
          <Input data-testid="factus-docs-input" value={docs} onChange={(e) => setDocs(e.target.value)} type="number" className="rounded-none mt-1" placeholder="100" />
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Período inicio</label>
          <Input data-testid="factus-start-input" value={start} onChange={(e) => setStart(e.target.value)} type="date" className="rounded-none mt-1" />
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Vencimiento</label>
          <Input data-testid="factus-end-input" value={end} onChange={(e) => setEnd(e.target.value)} type="date" className="rounded-none mt-1" />
        </div>
      </div>
      <div className="mt-4">
        <Button data-testid="factus-save-btn" onClick={save} disabled={saving} className="rounded-none bg-slate-900 hover:bg-slate-800 text-white">
          <Save className="mr-1 h-4 w-4" /> {saving ? 'Guardando…' : 'Guardar plan'}
        </Button>
      </div>
      {cfg.configured && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5 text-sm">
          <div><div className="text-[10px] uppercase font-bold text-slate-500">Usados</div><div className="font-mono text-lg">{num(snapshot.used)}</div></div>
          <div><div className="text-[10px] uppercase font-bold text-slate-500">Disponibles</div><div className="font-mono text-lg">{num(snapshot.available)}</div></div>
          <div><div className="text-[10px] uppercase font-bold text-slate-500">% consumo</div><div className="font-mono text-lg">{pct(snapshot.used_pct)}</div></div>
          <div><div className="text-[10px] uppercase font-bold text-slate-500">Bloqueadas</div><div className="font-mono text-lg text-rose-700">{num(cfg.blocked_invoices_count || 0)}</div></div>
        </div>
      )}
      {snapshot?.history?.length > 0 && (
        <div className="mt-5">
          <div className="text-[10px] uppercase font-bold text-slate-500 mb-2">Historial mensual</div>
          <table className="w-full text-sm" data-testid="factus-history-table">
            <thead>
              <tr className="text-left text-[10px] uppercase text-slate-500 border-b border-slate-200">
                <th className="py-1">Período</th><th>Facturas</th><th>Notas crédito</th><th>Total</th>
              </tr>
            </thead>
            <tbody>
              {snapshot.history.map((h) => (
                <tr key={h.period} className="border-b border-slate-100">
                  <td className="py-1 font-mono">{h.period}</td>
                  <td>{h.invoices}</td><td>{h.credit_notes}</td><td className="font-bold">{h.used}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ─── Integration card ─────────────────────────────────────────────────── */
function IntegrationCard({ s }) {
  const barPct = s.used_pct == null ? null : Math.min(100, s.used_pct);
  const barColor =
    barPct == null ? 'bg-slate-200' :
    barPct >= 100 ? 'bg-rose-600' :
    barPct >= 90  ? 'bg-rose-500' :
    barPct >= 80  ? 'bg-amber-500' :
    barPct >= 70  ? 'bg-amber-400' : 'bg-emerald-500';
  return (
    <div className="border border-slate-200 bg-white p-5" data-testid={`integration-card-${s.service}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
            {s.service}
          </div>
          <h3 className="font-bold text-slate-900 mt-0.5">{s.display_name}</h3>
          {s.plan && <div className="text-xs text-slate-500 mt-0.5">Plan: <span className="font-mono">{s.plan}</span></div>}
        </div>
        <StatusPill status={s.status} />
      </div>
      <div className="grid grid-cols-3 gap-3 mt-4 text-sm">
        <div><div className="text-[10px] uppercase font-bold text-slate-500">Usados</div><div className="font-mono">{num(s.used)}</div></div>
        <div><div className="text-[10px] uppercase font-bold text-slate-500">Disponibles</div><div className="font-mono">{num(s.available)}</div></div>
        <div><div className="text-[10px] uppercase font-bold text-slate-500">%</div><div className="font-mono">{pct(s.used_pct)}</div></div>
      </div>
      {barPct != null && (
        <div className="h-2 w-full bg-slate-100 mt-3">
          <div className={`h-2 ${barColor}`} style={{ width: `${barPct}%` }} />
        </div>
      )}
      <div className="mt-3 text-xs text-slate-600 flex items-center gap-2">
        <CalendarDays className="h-3 w-3" />
        {s.period_start ? `${s.period_start} → ${s.period_end || 'actual'}` : 'Sin período configurado'}
      </div>
      <div className="mt-2 flex items-center gap-2">
        {!s.has_real_balance_api && (
          <Badge variant="outline" className="rounded-none border-slate-300 text-[10px]">
            Consumo interno · sin API oficial de saldo
          </Badge>
        )}
        {s.errors_recent > 0 && (
          <Badge className="rounded-none bg-rose-100 text-rose-800 text-[10px]" data-testid={`integration-errors-${s.service}`}>
            {s.errors_recent} error(es) 24h
          </Badge>
        )}
      </div>
      {s.extra?.note && (
        <p className="text-[11px] text-slate-500 mt-3 italic">{s.extra.note}</p>
      )}
    </div>
  );
}

/* ─── Alerts list ──────────────────────────────────────────────────────── */
function AlertsList({ alerts, onAck }) {
  if (!alerts.length) {
    return (
      <div className="text-center text-slate-500 py-10 border border-dashed border-slate-200">
        <CheckCircle2 className="mx-auto h-8 w-8 text-emerald-500" />
        <p className="mt-2 text-sm">Sin alertas abiertas</p>
      </div>
    );
  }
  return (
    <div className="divide-y divide-slate-100 border border-slate-200 bg-white" data-testid="integration-alerts-list">
      {alerts.map((a) => (
        <div key={a.id} className="p-4 flex items-start gap-3" data-testid={`alert-row-${a.id}`}>
          <div className={`shrink-0 border px-2 py-1 text-[10px] font-bold uppercase tracking-wider ${LEVEL_STYLE[a.level]}`}>
            {a.level}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <div className="font-semibold text-slate-900">{a.service} · {a.reason}</div>
              <span className="text-[11px] text-slate-400 shrink-0">{formatDate(a.created_at)}</span>
            </div>
            <div className="text-sm text-slate-700 mt-0.5">{a.message}</div>
            {a.metric_value != null && (
              <div className="text-[11px] text-slate-500 mt-1 font-mono">
                {a.metric_name}: {a.metric_value}{a.threshold != null ? ` (umbral ${a.threshold})` : ''}
              </div>
            )}
          </div>
          {a.status === 'open' && (
            <Button
              size="sm" variant="outline"
              data-testid={`alert-ack-${a.id}`}
              className="rounded-none"
              onClick={() => onAck(a.id)}
            >
              Reconocer
            </Button>
          )}
          {a.status !== 'open' && (
            <span className="text-xs text-slate-400">{a.status}</span>
          )}
        </div>
      ))}
    </div>
  );
}

/* ─── Main page ────────────────────────────────────────────────────────── */
export default function AdminIntegrations() {
  useSEO({ title: 'Integraciones | AutoMarket Admin', canonical: '/admin/integraciones' });
  const [overview, setOverview] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [ov, al] = await Promise.all([
        api.get('/admin/integrations/overview'),
        api.get('/admin/integrations/alerts', { params: { status: 'open', limit: 50 } }),
      ]);
      setOverview(ov.data);
      setAlerts(al.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error cargando integraciones');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const ack = async (id) => {
    try {
      await api.post(`/admin/integrations/alerts/${id}/ack`);
      toast.success('Alerta reconocida');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const factusSnapshot = overview?.integrations?.find((s) => s.service === 'factus');

  return (
    <Layout>
      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Panel admin</div>
            <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1 inline-flex items-center gap-2">
              <Plug className="h-6 w-6" /> Centro de Integraciones
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Consumo, límites, vencimientos y alertas de servicios externos.
            </p>
          </div>
          <Button
            data-testid="integrations-reload-btn" onClick={load} variant="outline" className="rounded-none"
          >
            <RefreshCw className={`mr-1 h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Recargar
          </Button>
        </div>

        {/* Resumen alertas */}
        {overview && (
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="border border-slate-200 bg-white p-3" data-testid="metric-services">
              <div className="text-[10px] uppercase font-bold text-slate-500">Integraciones</div>
              <div className="text-2xl font-mono">{overview.services.length}</div>
            </div>
            <div className="border border-slate-200 bg-white p-3" data-testid="metric-alerts-open">
              <div className="text-[10px] uppercase font-bold text-slate-500">Alertas abiertas</div>
              <div className="text-2xl font-mono text-amber-700">{overview.alerts.open}</div>
            </div>
            <div className="border border-slate-200 bg-white p-3" data-testid="metric-alerts-critical">
              <div className="text-[10px] uppercase font-bold text-slate-500">Críticas</div>
              <div className="text-2xl font-mono text-rose-700">{overview.alerts.critical_open}</div>
            </div>
            <div className="border border-slate-200 bg-white p-3" data-testid="metric-mode">
              <div className="text-[10px] uppercase font-bold text-slate-500">DIAN</div>
              <div className="text-sm font-mono mt-1">
                {factusSnapshot?.extra?.dian_enabled ? 'ENABLED' : 'DISABLED'} · {factusSnapshot?.extra?.env || '—'}
              </div>
            </div>
          </div>
        )}

        {/* Factus panel (plan + consumo + historial) */}
        {factusSnapshot && (
          <div className="mt-6">
            <FactusQuotaPanel snapshot={factusSnapshot} onReload={load} />
          </div>
        )}

        {/* Alertas abiertas */}
        <div className="mt-8">
          <h2 className="font-display text-xl font-black tracking-tight text-slate-900 inline-flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" /> Alertas abiertas
          </h2>
          <div className="mt-3">
            <AlertsList alerts={alerts} onAck={ack} />
          </div>
        </div>

        {/* Cards del resto de integraciones */}
        <div className="mt-8">
          <h2 className="font-display text-xl font-black tracking-tight text-slate-900 inline-flex items-center gap-2">
            <Info className="h-5 w-5" /> Todas las integraciones
          </h2>
          <div className="mt-3 grid sm:grid-cols-2 gap-3" data-testid="integration-cards">
            {overview?.integrations?.map((s) => <IntegrationCard key={s.service} s={s} />)}
          </div>
        </div>
      </div>
    </Layout>
  );
}
